import { useState, useEffect } from 'react';

interface Commit {
  sha: string;
  message: string;
  author: string;
  date: string;
}

interface Contributor {
  login: string;
  avatar: string;
  contributions: number;
}

interface CodeFrequency {
  week: string;
  additions: number;
  deletions: number;
}

interface RepoInfo {
  stars: number;
  forks: number;
  open_issues: number;
  size_kb: number;
  default_branch: string;
  last_push: string;
  language: string;
}

interface DeveloperActivityData {
  commits: Commit[];
  contributors: Contributor[];
  code_frequency: CodeFrequency[];
  repo: RepoInfo;
  cached_at: string;
  error?: string;
}

export function DeveloperActivity({ refreshInterval = 300000 }: { refreshInterval?: number }) {
  const [data, setData] = useState<DeveloperActivityData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    try {
      const resp = await fetch('/api/developer-activity');
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const result = await resp.json();
      if (result.error && !result.commits?.length) {
        setError(result.error);
      } else {
        setData(result);
        setError(null);
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load developer activity');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval]);

  if (loading) return <div style={styles.container}><div style={styles.loading}>Loading developer activity...</div></div>;
  if (error) return <div style={styles.container}><div style={styles.error}>{error}</div></div>;
  if (!data) return null;

  const maxVal = Math.max(
    ...data.code_frequency.map(w => Math.max(w.additions, w.deletions)),
    1
  );
  const totalContributions = data.contributors.reduce((sum, c) => sum + c.contributions, 0) || 1;

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffH = Math.floor(diffMs / 3600000);
    if (diffH < 1) return `${Math.floor(diffMs / 60000)}m ago`;
    if (diffH < 24) return `${diffH}h ago`;
    const diffD = Math.floor(diffH / 24);
    if (diffD < 7) return `${diffD}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatSize = (kb: number) => {
    if (kb < 1024) return `${kb} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Developer Activity</h2>
        <span style={styles.repoName}>FanaticoInc/trade</span>
      </div>

      {data.repo && data.repo.last_push && (
        <div style={styles.statsRow}>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{data.commits.length}</div>
            <div style={styles.statLabel}>Commits (30d)</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{data.contributors.length}</div>
            <div style={styles.statLabel}>Contributors</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{data.repo.open_issues}</div>
            <div style={styles.statLabel}>Open Issues</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{formatSize(data.repo.size_kb)}</div>
            <div style={styles.statLabel}>Repo Size</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{formatDate(data.repo.last_push)}</div>
            <div style={styles.statLabel}>Last Push</div>
          </div>
        </div>
      )}

      {data.code_frequency.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Code Frequency (12 weeks)</h3>
          <div style={styles.chartContainer}>
            <svg width="100%" height="160" viewBox={`0 0 ${data.code_frequency.length * 60 + 20} 160`}>
              {data.code_frequency.map((week, i) => {
                const barWidth = 24;
                const x = i * 60 + 20;
                const addH = (week.additions / maxVal) * 120;
                const delH = (week.deletions / maxVal) * 120;
                return (
                  <g key={week.week}>
                    <rect x={x} y={130 - addH} width={barWidth} height={addH} fill="#a6e3a1" rx={2} opacity={0.9} />
                    <rect x={x + barWidth + 2} y={130 - delH} width={barWidth} height={delH} fill="#f38ba8" rx={2} opacity={0.9} />
                    <text x={x + barWidth} y={150} textAnchor="middle" fill="#a6adc8" fontSize="9">
                      {week.week.slice(5)}
                    </text>
                    {week.additions > 0 && (
                      <text x={x + barWidth / 2} y={130 - addH - 4} textAnchor="middle" fill="#a6e3a1" fontSize="8">
                        +{week.additions > 999 ? `${(week.additions / 1000).toFixed(1)}k` : week.additions}
                      </text>
                    )}
                  </g>
                );
              })}
            </svg>
            <div style={styles.legend}>
              <span style={{ ...styles.legendItem, color: '#a6e3a1' }}>+ Additions</span>
              <span style={{ ...styles.legendItem, color: '#f38ba8' }}>- Deletions</span>
            </div>
          </div>
        </div>
      )}

      {data.contributors.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Contributors</h3>
          <div style={styles.contributorsList}>
            {data.contributors.map((c) => (
              <div key={c.login} style={styles.contributorRow}>
                <img src={c.avatar} alt={c.login} style={styles.avatar} />
                <span style={styles.contributorName}>{c.login}</span>
                <div style={styles.contributorBarContainer}>
                  <div
                    style={{
                      ...styles.contributorBar,
                      width: `${(c.contributions / totalContributions) * 100}%`,
                    }}
                  />
                </div>
                <span style={styles.contributorCount}>{c.contributions}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.commits.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Recent Commits</h3>
          <div style={styles.commitsList}>
            {data.commits.slice(0, 15).map((commit) => (
              <div key={commit.sha} style={styles.commitRow}>
                <code style={styles.sha}>{commit.sha}</code>
                <span style={styles.commitMessage}>{commit.message}</span>
                <span style={styles.commitMeta}>
                  {commit.author} &middot; {formatDate(commit.date)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={styles.footer}>
        Cached: {data.cached_at ? formatDate(data.cached_at) : 'N/A'} &middot; Refreshes every 5 min
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    background: '#1e1e2e',
    borderRadius: 12,
    padding: 20,
    color: '#cdd6f4',
    fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  title: {
    margin: 0,
    fontSize: 18,
    color: '#cdd6f4',
  },
  repoName: {
    fontSize: 13,
    color: '#89b4fa',
    background: '#313244',
    padding: '4px 10px',
    borderRadius: 6,
  },
  loading: {
    textAlign: 'center' as const,
    padding: 40,
    color: '#a6adc8',
  },
  error: {
    textAlign: 'center' as const,
    padding: 40,
    color: '#f38ba8',
  },
  statsRow: {
    display: 'flex',
    gap: 12,
    marginBottom: 20,
    flexWrap: 'wrap' as const,
  },
  statCard: {
    flex: 1,
    minWidth: 100,
    background: '#313244',
    borderRadius: 8,
    padding: '12px 16px',
    textAlign: 'center' as const,
  },
  statValue: {
    fontSize: 20,
    fontWeight: 700,
    color: '#89b4fa',
  },
  statLabel: {
    fontSize: 11,
    color: '#a6adc8',
    marginTop: 4,
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 14,
    color: '#a6adc8',
    marginBottom: 10,
    borderBottom: '1px solid #313244',
    paddingBottom: 6,
  },
  chartContainer: {
    background: '#181825',
    borderRadius: 8,
    padding: 12,
    overflowX: 'auto' as const,
  },
  legend: {
    display: 'flex',
    gap: 16,
    justifyContent: 'center',
    marginTop: 8,
    fontSize: 11,
  },
  legendItem: {
    fontSize: 11,
  },
  contributorsList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 8,
  },
  contributorRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '6px 0',
  },
  avatar: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    border: '2px solid #313244',
  },
  contributorName: {
    fontSize: 13,
    color: '#cdd6f4',
    minWidth: 120,
  },
  contributorBarContainer: {
    flex: 1,
    height: 8,
    background: '#313244',
    borderRadius: 4,
    overflow: 'hidden' as const,
  },
  contributorBar: {
    height: '100%',
    background: '#89b4fa',
    borderRadius: 4,
    transition: 'width 0.3s ease',
  },
  contributorCount: {
    fontSize: 12,
    color: '#a6adc8',
    minWidth: 40,
    textAlign: 'right' as const,
  },
  commitsList: {
    maxHeight: 400,
    overflowY: 'auto' as const,
  },
  commitRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 10,
    padding: '6px 0',
    borderBottom: '1px solid #181825',
    flexWrap: 'wrap' as const,
  },
  sha: {
    fontSize: 12,
    color: '#f9e2af',
    background: '#313244',
    padding: '2px 6px',
    borderRadius: 4,
    flexShrink: 0,
  },
  commitMessage: {
    fontSize: 13,
    color: '#cdd6f4',
    flex: 1,
    minWidth: 200,
  },
  commitMeta: {
    fontSize: 11,
    color: '#6c7086',
    flexShrink: 0,
  },
  footer: {
    textAlign: 'center' as const,
    fontSize: 11,
    color: '#6c7086',
    marginTop: 16,
    paddingTop: 12,
    borderTop: '1px solid #313244',
  },
};
